"use client";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Tag, TriangleAlert } from "lucide-react";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";
import { ASUS_GX10_MODEL, ASUS_GX10_PRICE_LINE } from "@/content/enterprise/asus-gx10-offer";

const DGX_WHATSAPP_MESSAGE =
  "Hi Anchor Sprint, I'd like a quote for NVIDIA DGX Spark (from your DGX Spark page).";
import {
  dgxSparkSpecs,
  dgxSparkCapacity,
  dgxSparkOems,
  dgxSparkFaqs,
  dgxSparkPromo,
} from "@/content/enterprise/dgx-spark";

// Buying steps mirror the enterprise hub so the procurement promise reads the
// same everywhere — it is the part no box-shifting competitor publishes.
const buyingSteps = [
  { step: "01", title: "Tell us the model", detail: "Which OEM build, how many, and whether you need two linked." },
  { step: "02", title: "Quotation in RM", detail: "Priced locally, usually within two working days, SST stated." },
  { step: "03", title: "Purchase order", detail: "Standard PO terms; MyInvois e-invoice issued." },
  { step: "04", title: "Authorised supply", detail: "Sourced through authorised Malaysian distribution." },
  { step: "05", title: "Delivered and set up", detail: "Racked or on a desk, on your network, DGX OS ready." },
  { step: "06", title: "Put to work", detail: "Optional: we get a model serving something useful behind it." },
];

export default function DgxSparkMalaysia() {
  const locale = useLocale();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5 },
      };
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP). Below-the-fold sections keep the inView fade.
  const heroIn = reduce
    ? {}
    : {
        initial: { y: 12 },
        animate: { y: 0 },
        transition: { duration: 0.5 },
      };
  const lp = (href: string) => getLocalizedPathname(href, locale);
  const enquiry = lp("/enterprise/dgx-spark-malaysia/enquiry/");

  return (
    <main className="bg-background">
      {/* ── Hero ── */}
      <section className="pb-14 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <motion.div {...heroIn}>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {t(locale, "Enterprise Solutions · Data & AI")}
              </p>
              <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl">
                {t(locale, "NVIDIA DGX Spark in Malaysia — every OEM build, quoted in RM")}
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "A petaFLOP of AI compute and 128 GB of unified memory in a 1.2 kg box that runs off a wall socket. As an authorised NVIDIA DGX Spark reseller, we supply the NVIDIA Founders Edition and every OEM version — ASUS, Dell, HP, Lenovo, Acer, Gigabyte and MSI — with SST and MyInvois handled.",
                )}
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                <Link
                  href={enquiry}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  {t(locale, "Request a quotation")}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <WhatsAppCta
                  message={DGX_WHATSAPP_MESSAGE}
                  location="dgx_spark_hero"
                  label={t(locale, "WhatsApp us")}
                  className="px-8"
                />
                <a
                  href="#models"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-8 py-3 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
                >
                  {t(locale, "Compare the OEM models")}
                </a>
              </div>
              <p className="mt-5 text-sm text-neutral-muted">
                Looking for a laptop? <Link href="/enterprise/rtx-spark-laptops-malaysia/" className="font-semibold text-primary underline underline-offset-4">Compare RTX Spark laptops in Malaysia</Link>.
              </p>
            </motion.div>

            {/* Dark proof-moment card — the one place the design language allows
                a dark panel, and the official render is shot on black. */}
            <motion.figure {...heroIn} className="overflow-hidden rounded-xl border border-border bg-[#0b0b0c]">
              <img
                src="/images/enterprise/dgx-spark-hero.jpg"
                alt={t(
                  locale,
                  "NVIDIA DGX Spark desktop AI supercomputer — a compact gold-mesh chassis beside a laptop",
                )}
                width={1176}
                height={750}
                loading="eager"
                className="h-full w-full object-cover"
              />
              <figcaption className="border-t border-white/10 px-5 py-3 text-xs text-white/60">
                {t(locale, "NVIDIA DGX Spark. Product image courtesy of NVIDIA Corporation.")}
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </section>

      {/* ── Promotion ── */}
      {dgxSparkPromo.active && (
        <section className="pb-4">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              {...heroIn}
              className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-5 sm:flex-row sm:items-start"
            >
              <Tag className="h-5 w-5 flex-none text-primary" aria-hidden />
              <div>
                <p className="font-semibold text-neutral-emphasis">
                  {t(locale, "On promotion now")}{" "}
                  <span className="font-normal text-neutral-muted">
                    ({t(locale, dgxSparkPromo.asOf)})
                  </span>
                </p>
                <p className="mt-1 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, dgxSparkPromo.body)}
                </p>
              </div>
              <Link
                href={enquiry}
                className="mt-1 inline-flex flex-none items-center gap-2 self-start rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis sm:ml-auto"
              >
                {t(locale, "Get current pricing")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── What it is ── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "What DGX Spark actually is")}
            </h2>
            <p className="mt-3 text-neutral-muted">
              {t(
                locale,
                "A desktop AI supercomputer built on the NVIDIA GB10 Grace Blackwell Superchip. The point of it is the 128 GB of memory shared coherently between CPU and GPU: it lets you hold models on your own desk that would otherwise need rented data-centre time. These specifications are fixed by NVIDIA and identical in every OEM build.",
              )}
            </p>
          </motion.div>

          <div className="mt-10 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
            <motion.dl {...inView} className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2">
              {dgxSparkSpecs.map((s) => (
                <div key={s.label} className="bg-card p-4">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                    {t(locale, s.label)}
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-neutral-emphasis">{t(locale, s.value)}</dd>
                </div>
              ))}
            </motion.dl>

            <motion.div {...inView} className="rounded-lg border border-border bg-neutral-surface p-6">
              <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                {t(locale, "What 128 GB buys you")}
              </h3>
              <ul className="mt-4 space-y-4">
                {dgxSparkCapacity.map((c) => (
                  <li key={c.label}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                      {t(locale, c.label)}
                    </p>
                    <p className="mt-0.5 text-sm font-medium text-neutral-emphasis">{t(locale, c.value)}</p>
                  </li>
                ))}
              </ul>
              <p className="mt-5 border-t border-border pt-4 text-xs leading-relaxed text-neutral-muted">
                {t(locale, "Figures published by NVIDIA for the GB10 platform.")}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── OEM models ── */}
      <section id="models" className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "Every DGX Spark model we can quote")}
            </h2>
            <p className="mt-3 text-neutral-muted">
              {t(
                locale,
                "NVIDIA fixes the superchip, the 128 GB of unified memory and the ConnectX-7 networking across all of these, so raw compute does not change with the badge. OEMs differ on storage capacity, cooling and acoustics, chassis, warranty terms and remote management.",
              )}
            </p>
            {/* Routes the cluster's traffic leader to its conversion leader.
                GSC 28d: this page draws 1,005 impressions (most in /enterprise/),
                while /enterprise/nvidia-malaysia/ draws 381. GA4 28d: the NVIDIA
                brand page converts at 27% (10 key events / 37 sessions) -- the
                highest on the site -- and had only 5 internal links pointing at
                it. This page names NVIDIA 39 times and linked to it zero times,
                because the OEM grid links box builders and NVIDIA is the
                platform, so it fell through every existing link path. */}
            <p className="mt-3 text-neutral-muted">
              {t(
                locale,
                "The platform underneath every one of these is NVIDIA's, from workstation GPUs to rack systems.",
              )}{" "}
              <Link
                href={lp("/enterprise/nvidia-malaysia/")}
                className="text-primary underline transition hover:text-blue-800"
              >
                {t(locale, "See the full NVIDIA line we supply")}
              </Link>
              .
            </p>
          </motion.div>

          <motion.ul {...inView} className="mt-10 grid gap-4 md:grid-cols-2">
            {dgxSparkOems.map((o) => (
              <li
                key={`${o.brand}-${o.model}`}
                className="flex h-full flex-col rounded-lg border border-border bg-card p-5"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                  {o.brand}
                </p>
                <h3 className="mt-1 font-display text-lg font-semibold leading-tight text-neutral-emphasis">
                  {o.page ? (
                    <Link
                      href={lp(`/enterprise/${o.page}/`)}
                      className="transition hover:text-primary"
                    >
                      {o.model}
                    </Link>
                  ) : (
                    o.model
                  )}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, o.note)}
                </p>
                {o.model === ASUS_GX10_MODEL && (
                  <p className="mt-3 text-sm font-semibold text-primary">
                    {t(locale, ASUS_GX10_PRICE_LINE)}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                  {o.page && (
                    <Link
                      href={lp(`/enterprise/${o.page}/`)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                    >
                      {t(locale, "Read the full page")}
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  )}
                  <Link
                    href={lp(o.model === ASUS_GX10_MODEL
                      ? "/enterprise/asus-ascent-gx10-malaysia/#gx10-quotation"
                      : `/enterprise/dgx-spark-malaysia/enquiry/?model=${encodeURIComponent(o.model)}`)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-muted hover:text-primary"
                  >
                    {t(locale, o.model === ASUS_GX10_MODEL ? "Choose storage and request a quote" : "Quote this model")}
                  </Link>
                </div>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ── The honest caveat ── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6 sm:flex-row sm:p-8"
          >
            <TriangleAlert className="h-6 w-6 flex-none text-primary" aria-hidden />
            <div className="max-w-3xl">
              <h2 className="font-display text-xl font-bold text-neutral-emphasis md:text-2xl">
                {t(locale, "When DGX Spark is the wrong purchase")}
              </h2>
              <p className="mt-3 leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "Memory bandwidth is 273 GB/s. That is generous for the size and price class and it is why the machine is excellent for prototyping, for fine-tuning up to around 70 billion parameters, and for keeping data on your own premises. But bandwidth, not capacity, is what governs inference throughput once a model is loaded — and a data-centre GPU has several times more of it.",
                )}
              </p>
              <p className="mt-3 leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "So if the plan is high-volume production inference for many concurrent users, this is the wrong box and we will say so before you buy it rather than after. We would rather quote you the right thing than the expensive thing.",
                )}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tender/RFQ spoke. This hub holds the head term and the authority; the
          tender page holds a different intent (a procurement officer with a
          document to write, not a buyer). Contextual link, not a footer one. */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="rounded-lg border border-primary/30 bg-primary/5 p-6 sm:p-8"
          >
            <h2 className="font-display text-xl font-bold text-neutral-emphasis md:text-2xl">
              {t(locale, "Buying through a tender or an RFQ?")}
            </h2>
            <p className="mt-3 max-w-3xl leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Then you need a specification you can lift into the document, an equivalence clause that will not lock the tender to one brand, and an OEM Letter of Authorisation. We publish all three, and return a formal quotation within one working day.",
              )}
            </p>
            <Link
              href={lp("/enterprise/dgx-spark-tender-malaysia/")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {t(locale, "DGX Spark tender & RFQ support")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── How buying works ── */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView}>
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "How buying works in Malaysia")}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {t(
                locale,
                "Company purchase, not a consumer checkout — the paperwork a finance department needs is part of the quote.",
              )}
            </p>
          </motion.div>
          <motion.ol
            {...inView}
            className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3"
          >
            {buyingSteps.map((s) => (
              <li key={s.step} className="bg-card p-6">
                <span className="font-display text-sm font-bold text-primary">{s.step}</span>
                <h3 className="mt-2 font-semibold text-neutral-emphasis">{t(locale, s.title)}</h3>
                <p className="mt-1 text-sm text-neutral-muted">{t(locale, s.detail)}</p>
              </li>
            ))}
          </motion.ol>
        </div>
      </section>

      {/* ── Bridge to what we actually do ── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "Buying the box is the easy half")}
            </h2>
            <p className="mt-3 leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Most DGX Spark units sold in Malaysia will spend their first six months running a demo. We are Certified in the Claude Partner Network and we build agentic AI systems that run in production, so we can quote the hardware and then make it earn its keep — a model serving a real workflow behind your own network, not a benchmark screenshot.",
              )}
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                href={lp("/services/ai-agents/")}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                {t(locale, "See our AI agent services")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link
                href={lp("/blog/nvidia-rtx-spark-local-ai-malaysian-businesses/")}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                {t(locale, "Read the local-vs-cloud decision guide")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Schema co-located in the component so /ms and /zh render it too. */}
          <StructuredData type="faq" faqs={dgxSparkFaqs} />
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Common questions")}
          </motion.h2>
          <motion.dl {...inView} className="mt-8 grid gap-6 lg:grid-cols-2">
            {dgxSparkFaqs.map((f) => (
              <div key={f.question} className="rounded-lg border border-border bg-card p-6">
                <dt className="font-semibold text-neutral-emphasis">{t(locale, f.question)}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.answer)}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="rounded-xl border border-border bg-card p-8 md:p-10">
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "Get a DGX Spark quotation in Ringgit")}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {t(
                locale,
                "Tell us the model and quantity. We come back in RM with SST stated, usually within two working days — and we will tell you if a Spark is not the right machine for what you described.",
              )}
            </p>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {[
                "Every OEM build quotable",
                "Quoted in RM, SST stated",
                "MyInvois e-invoice on purchase",
                "Authorised Malaysian distribution",
              ].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-neutral-muted">
                  <Check className="h-4 w-4 flex-none text-primary" aria-hidden />
                  {t(locale, x)}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                href={enquiry}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Request a quotation")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <WhatsAppCta
                message={DGX_WHATSAPP_MESSAGE}
                location="dgx_spark_closing_cta"
                label={t(locale, "WhatsApp us")}
                className="px-8"
              />
            </div>
            <p className="mt-8 border-t border-border pt-6 text-xs leading-relaxed text-neutral-muted">
              {t(
                locale,
                "NVIDIA, DGX, DGX Spark, Grace, Blackwell and ConnectX are trademarks of NVIDIA Corporation. ThinkStation is a trademark of Lenovo; Ascent of ASUSTeK; Pro Max of Dell Technologies; ZGX of HP Inc.; Veriton of Acer; AI TOP of GIGABYTE. Anchor Sprint is an authorised NVIDIA DGX Spark reseller in Malaysia, and an authorised Dell and Lenovo reseller; this page does not imply endorsement by NVIDIA Corporation or these manufacturers.",
              )}
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
